import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Mail, Calendar, MessageSquare, Phone, MapPin, Clock, Send, Shield, AlertTriangle, Users, Eye, FileText, Zap } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const ContactUsPage = () => {
  const [openSections, setOpenSections] = useState({
    general: true,
    editorial: false,
    partnerships: false,
    technical: false,
    privacy: false,
    media: false,
    accessibility: false,
    security: false,
    response: false,
    medical: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />

      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Contact Us
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                We're here to help and we'd love to hear from you.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="flex justify-start md:justify-end w-full md:w-auto md:flex-shrink-0 items-end">
              <a 
                href="mailto:hello@meetradiance.com" 
                className="inline-flex items-center justify-center bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-medium px-6 py-3 rounded-lg text-sm transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full md:w-auto"
              >
                Send Us a Message
                <Send className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: September 9, 2025</span>
          </div>

          {/* Introduction */}
          <div className="mb-8 p-4 bg-white border border-gray-300 rounded-lg shadow-sm">
            <p className="text-sm text-gray-600">
              Radiance operates a single point of contact to keep things fast and consistent. No matter the topic, start with <a href="mailto:hello@meetradiance.com" className="text-[#ab3d22] hover:underline font-medium">hello@meetradiance.com</a> and we'll route it internally.
            </p>
          </div>

          {/* General Inquiries */}
          <AccordionItem
            title="General Inquiries"
            isOpen={openSections.general}
            onToggle={() => toggleSection('general')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Have a question about our content, product categories, or how Radiance works? Say hello!
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Email:</span>
                    </div>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all sm:ml-0 ml-6">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">within 24 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <p className="text-sm">
                  <strong>Helpful to include:</strong> your name, the page or product you're asking about (URL if possible), and a short description of your question.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Editorial Questions & Corrections */}
          <AccordionItem
            title="Editorial Questions & Corrections"
            isOpen={openSections.editorial}
            onToggle={() => toggleSection('editorial')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We take accuracy seriously. If you spot an error or want to understand our methodology, we want to hear from you.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Editorial & methodology:</span>
                    </div>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all sm:ml-0 ml-6">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Corrections:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(use subject line: CORRECTION)</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">within 48 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <p className="text-sm">
                  <strong>How we handle corrections:</strong> We review the report, verify with reliable sources, update the content, and—where appropriate—add a dated correction note per our Editorial Standards.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Partnerships & Advertising */}
          <AccordionItem
            title="Partnerships & Advertising"
            isOpen={openSections.partnerships}
            onToggle={() => toggleSection('partnerships')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Interested in sponsored content, display, newsletter, or social activations? Let's talk.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Advertising & sponsored content:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: ADVERTISING)</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Affiliate & brand partnerships:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: PARTNERSHIP)</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">within 48 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <p className="text-sm">
                  <strong>Partner criteria:</strong> We only collaborate with brands aligned to our audience and values. Sponsored work is clearly labeled and never influences our independent rankings or reviews.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Technical Support */}
          <AccordionItem
            title="Technical Support"
            isOpen={openSections.technical}
            onToggle={() => toggleSection('technical')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If something on the site isn't working, we'll help you troubleshoot.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Tech support:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: TECH SUPPORT)</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">within 24 hours</span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Common issues we can help with:</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Page loading or display problems</li>
                  <li>• Search not returning expected results</li>
                  <li>• Newsletter subscription/delivery issues</li>
                  <li>• Account or saved lists access (if applicable)</li>
                </ul>
              </div>
              
              <CalloutBox>
                <p className="text-sm">
                  <strong>Helpful to include:</strong> device, browser version, screenshots, steps to reproduce, and the page URL.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Privacy, Data, and Legal Requests */}
          <AccordionItem
            title="Privacy, Data, and Legal Requests"
            isOpen={openSections.privacy}
            onToggle={() => toggleSection('privacy')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                For privacy questions, data subject requests (access, correction, deletion), or legal notices:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Privacy & data requests:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: PRIVACY)</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Legal notices:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: LEGAL)</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 ml-6">3–5 business days depending on complexity</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Tip</p>
                    <p className="text-sm">
                      For data requests, please include the email address you used with Radiance and any relevant page URLs. Do not include sensitive info beyond what's necessary.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Media & Press */}
          <AccordionItem
            title="Media & Press"
            isOpen={openSections.media}
            onToggle={() => toggleSection('media')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                For interviews, quotes, or commentary about beauty and wellness trends:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Press & media:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: PRESS)</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">3–5 business days</span>
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          {/* Accessibility */}
          <AccordionItem
            title="Accessibility"
            isOpen={openSections.accessibility}
            onToggle={() => toggleSection('accessibility')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We're committed to an accessible experience. If you encounter a barrier:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Accessibility support:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: ACCESSIBILITY)</span>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 sm:ml-0 ml-6">within 48 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <p className="text-sm">
                  Please share the page URL, the assistive technology you use (if any), and a brief description of the issue.
                </p>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Responsible Disclosure (Security) */}
          <AccordionItem
            title="Responsible Disclosure (Security)"
            isOpen={openSections.security}
            onToggle={() => toggleSection('security')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                If you believe you've found a security or privacy vulnerability:
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Security reports:</span>
                    </div>
                    <div className="ml-6 space-y-1">
                      <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline break-all block">
                        hello@meetradiance.com
                      </a>
                      <span className="text-xs text-gray-500 block">(subject: SECURITY)</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-[#ab3d22] flex-shrink-0" />
                      <span className="text-sm font-medium">Target response:</span>
                    </div>
                    <span className="text-sm text-gray-600 ml-6">acknowledgment within 72 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Important</p>
                    <p className="text-sm">
                      Please include a clear description and steps to reproduce. Do not publicly disclose before we've had a chance to investigate and remediate.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Response Times & Expectations */}
          <AccordionItem
            title="Response Times & Expectations"
            isOpen={openSections.response}
            onToggle={() => toggleSection('response')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We aim to reply as quickly as possible. Typical windows are:
              </p>
              
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Within 24 hours</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• General inquiries, technical support, newsletter/account issues</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Within 48 hours</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Editorial questions, corrections, advertising & partnerships, accessibility</li>
                  </ul>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Within 3–5 business days</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Complex business proposals, detailed editorial reviews, legal/privacy, media & press, security investigations</li>
                  </ul>
                </div>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Urgent?</p>
                    <p className="text-sm">
                      Add URGENT to your subject line. We'll prioritize it.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Medical Information Note */}
          <AccordionItem
            title="A quick note on medical information"
            isOpen={openSections.medical}
            onToggle={() => toggleSection('medical')}
          >
            <div className="space-y-4">
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Medical Disclaimer</p>
                    <p className="text-sm">
                      Radiance provides educational content only. We do not offer medical advice or diagnosis. For health concerns, consult a qualified healthcare professional.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default ContactUsPage;
